import { useCallback } from "react";
import {
  getMarketOverview,
  getMovers,
  getNews,
  getSectors,
  getSentiment,
  getCommodities,
  getCurrencies,
  getYields,
  getEarnings,
  getCrypto,
  getSocialSentiment,
} from "../services/pulseService";
import { usePolling } from "../components/pulse/usePolling";
import { PulseHeader } from "../components/pulse/PulseHeader";
import { PulseSkeleton } from "../components/pulse/PulseSkeleton";
import { QuoteTable } from "../components/pulse/QuoteTable";
import { AccountDailyPL } from "../components/pulse/AccountDailyPL";
import { SectorHeatmap } from "../components/pulse/SectorHeatmap";
import { FearGreedGauge } from "../components/pulse/FearGreedGauge";
import { MoversPanel } from "../components/pulse/MoversPanel";
import { NewsFeed } from "../components/pulse/NewsFeed";
import { CryptoTicker } from "../components/pulse/CryptoTicker";
import { EarningsCalendar } from "../components/pulse/EarningsCalendar";
import { EconomyWidget } from "../components/pulse/EconomyWidget";
import { SocialTrending } from "../components/pulse/SocialTrending";
import { SymbolChart } from "../components/pulse/SymbolChart";

// Polling intervals
const FAST = 30_000; // 30s for market data
const MEDIUM = 60_000; // 60s for movers, crypto
const SLOW = 120_000; // 2min for news, sentiment
const VERY_SLOW = 300_000; // 5min for earnings, economy

export function PulsePage() {
  // Market overview (indices, futures, global)
  const market = usePolling({ fetcher: getMarketOverview, interval: FAST });

  // Movers (gainers, losers, most active)
  const movers = usePolling({ fetcher: getMovers, interval: MEDIUM });

  // Market news
  const news = usePolling({ fetcher: getNews, interval: SLOW });

  // Sectors
  const sectors = usePolling({ fetcher: getSectors, interval: MEDIUM });

  // Sentiment (fear & greed + VIX)
  const sentiment = usePolling({ fetcher: getSentiment, interval: SLOW });

  // Social trending
  const social = usePolling({ fetcher: getSocialSentiment, interval: SLOW });

  // Commodities & Currencies & Yields
  const commodities = usePolling({ fetcher: getCommodities, interval: VERY_SLOW });
  const currencies = usePolling({ fetcher: getCurrencies, interval: VERY_SLOW });
  const yields = usePolling({ fetcher: getYields, interval: VERY_SLOW });

  // Earnings calendar (this week)
  const earnings = usePolling({
    fetcher: useCallback(() => {
      const now = new Date();
      const from = now.toISOString().slice(0, 10);
      const to = new Date(now.getTime() + 7 * 86400000).toISOString().slice(0, 10);
      return getEarnings(from, to);
    }, []),
    interval: VERY_SLOW,
  });

  // Crypto
  const crypto = usePolling({ fetcher: useCallback(() => getCrypto(15), []), interval: MEDIUM });

  const isInitialLoading = market.isLoading && !market.data;

  const handleRefresh = () => {
    market.refetch();
    movers.refetch();
    news.refetch();
    sectors.refetch();
    sentiment.refetch();
    social.refetch();
    commodities.refetch();
    currencies.refetch();
    yields.refetch();
    earnings.refetch();
    crypto.refetch();
  };

  if (isInitialLoading) {
    return <PulseSkeleton />;
  }

  // Extract SPY quote from market data for account comparison
  const spyQuote = market.data?.indices?.find((q) => q.symbol === "SPY");

  return (
    <div className="pb-8">
      <PulseHeader
        lastUpdated={market.lastUpdated}
        isLoading={market.isLoading}
        onRefresh={handleRefresh}
      />

      <div className="px-4 sm:px-6 lg:px-8 space-y-4 sm:space-y-6">
        {/* ── Row 1: Market Overview ─────────────────────────── */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {market.data ? (
            <>
              <QuoteTable title="US Indices" quotes={market.data.indices} />
              <QuoteTable title="Futures" quotes={market.data.futures} compact />
              <QuoteTable title="Global Markets" quotes={market.data.global} compact />
            </>
          ) : (
            <>
              <QuoteTable title="US Indices" quotes={[]} />
              <QuoteTable title="Futures" quotes={[]} compact />
              <QuoteTable title="Global Markets" quotes={[]} compact />
            </>
          )}
        </div>

        {/* ── Account P/L vs SPY ─────────────────────────────── */}
        <AccountDailyPL spyQuote={spyQuote} />

        {/* ── Row 2: Chart + Sectors + Sentiment ─────────────── */}
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
          <SymbolChart />
          <div className="grid grid-cols-1 gap-4">
            {sectors.data ? (
              <SectorHeatmap sectors={sectors.data.sectors} />
            ) : (
              <SectorHeatmap sectors={[]} />
            )}
            <FearGreedGauge
              fearGreed={sentiment.data?.fear_greed}
              vix={sentiment.data?.vix}
            />
          </div>
        </div>

        {/* ── Row 3: Movers + News ───────────────────────────── */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <MoversPanel
            gainers={movers.data?.gainers || []}
            losers={movers.data?.losers || []}
            mostActive={movers.data?.most_active || []}
          />
          <NewsFeed articles={news.data?.articles || []} />
        </div>

        {/* ── Row 4: Economy (Yields + Commodities + Currencies) */}
        <EconomyWidget
          commodities={commodities.data?.commodities || []}
          currencies={currencies.data?.currencies || []}
          yields={yields.data || null}
        />

        {/* ── Row 5: Crypto + Social + Earnings ──────────────── */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          <CryptoTicker coins={crypto.data?.coins || []} />
          <SocialTrending trending={social.data?.trending || []} />
          <EarningsCalendar events={earnings.data?.events || []} />
        </div>

        {/* ── Error indicator ────────────────────────────────── */}
        {market.error && (
          <div className="fixed bottom-20 right-4 z-50 px-4 py-2 rounded-lg bg-red-500/20 border border-red-500/30 text-red-300 text-sm backdrop-blur-xl animate-slide-up">
            Connection issue — retrying...
          </div>
        )}
      </div>
    </div>
  );
}
